"use client"

import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { useAuth } from "@/contexts/AuthContext"
import { usePlannerEvent } from "@/contexts/PlannerEventContext"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Plus, Trash2, Edit, CheckCircle2, Download } from "lucide-react"
import {
  fixSerbianText,
  drawPDFHeader,
  drawPDFFooter,
  drawStatRow,
  getPDFTableStyles,
  withPDFRelaxedRows,
  loadPDFLogo,
  loadPDF,
  C,
} from "@/lib/pdf-utils"
import {
  getTodos,
  createTodo,
  updateTodo,
  deleteTodo,
  toggleTodo,
  type Todo,
} from "@/lib/api"
import { PlannerEventRequiredNotice } from "@/components/PlannerEventRequiredNotice"
import { EventDataLoading } from "@/components/EventDataLoading"

type FilterType = "all" | "completed" | "not-completed"
type SortType = "newest" | "oldest"

export default function ZadaciPage() {
  const { user, token, isLoading: authLoading } = useAuth()
  const {
    activeEventNumericId,
    activeSummary,
    listReady: plannerListReady,
    summaries: plannerSummaries,
  } = usePlannerEvent();
  const hasNoEvents = plannerListReady && plannerSummaries.length === 0;
  const activeEventNumericIdRef = useRef(activeEventNumericId);
  useEffect(() => {
    activeEventNumericIdRef.current = activeEventNumericId;
  }, [activeEventNumericId]);

  const router = useRouter()
  const [todos, setTodos] = useState<Todo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null)
  const [deleteTodoId, setDeleteTodoId] = useState<number | null>(null)
  const [filter, setFilter] = useState<FilterType>("all")
  const [sort, setSort] = useState<SortType>("newest")

  // Form state
  const [todoTitle, setTodoTitle] = useState("")
  const [todoTitleError, setTodoTitleError] = useState<string | null>(null)

  useEffect(() => {
    if (!authLoading && !user) {
      router.push("/prijava")
    }
  }, [user, authLoading, router])

  useEffect(() => {
    if (
      user &&
      token &&
      plannerListReady &&
      activeEventNumericId != null
    ) {
      setLoading(true)
      setTodos([])
      setError(null)
      void loadTodos()
    } else if (user && token && plannerListReady && activeEventNumericId == null) {
      setTodos([])
      setLoading(false)
    }
  }, [user, token, plannerListReady, activeEventNumericId])

  const loadTodos = async () => {
    if (!token || !user) return
    const forEvent = activeEventNumericIdRef.current
    if (forEvent == null) return

    try {
      setLoading(true)
      setError(null)
      const todosData = await getTodos(token, user.id, forEvent)
      if (activeEventNumericIdRef.current !== forEvent) return
      setTodos(todosData)
    } catch (err) {
      console.error("Error loading todos:", err)
      if (activeEventNumericIdRef.current === forEvent) {
        setError(
          err instanceof Error
            ? err.message
            : "Greška pri učitavanju zadataka"
        )
      }
    } finally {
      if (activeEventNumericIdRef.current === forEvent) {
        setLoading(false)
      }
    }
  }

  const handleCreateTodo = async () => {
    if (!token || !user || !todoTitle.trim()) {
      setTodoTitleError("Naziv zadatka je obavezan")
      return
    }

    if (hasNoEvents) {
      setError("Prvo kreirajte proslavu na stranici Profil.");
      return;
    }

    if (activeEventNumericId == null) {
      setError("Izaberite ili kreirajte događaj na strani Profil.")
      return
    }

    try {
      setError(null)
      setTodoTitleError(null)
      const newTodo = await createTodo(
        token,
        todoTitle.trim(),
        user.id,
        activeEventNumericId,
        activeSummary?.documentId
      )
      setTodos([...todos, newTodo])
      setTodoTitle("")
      setIsCreateDialogOpen(false)
    } catch (err) {
      console.error("Error creating todo:", err)
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri kreiranju zadatka"
      )
    }
  }

  const handleUpdateTodo = async () => {
    if (!token || !editingTodo || !todoTitle.trim()) {
      setTodoTitleError("Naziv zadatka je obavezan")
      return
    }

    if (hasNoEvents) return;

    try {
      setError(null)
      setTodoTitleError(null)
      const updatedTodo = await updateTodo(
        token,
        editingTodo.id,
        todoTitle.trim(),
        editingTodo.attributes?.completed ?? editingTodo.completed ?? false,
        editingTodo.documentId
      )
      setTodos(
        todos.map((t) =>
          (t.id === updatedTodo.id || t.documentId === updatedTodo.documentId)
            ? updatedTodo
            : t
        )
      )
      setTodoTitle("")
      setEditingTodo(null)
      setIsEditDialogOpen(false)
    } catch (err) {
      console.error("Error updating todo:", err)
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri ažuriranju zadatka"
      )
    }
  }

  const handleToggleTodo = async (todo: Todo) => {
    if (!token || hasNoEvents) return;

    try {
      setError(null)
      const currentCompleted =
        todo.attributes?.completed ?? todo.completed ?? false
      const updatedTodo = await toggleTodo(
        token,
        todo.id,
        !currentCompleted,
        todo.documentId
      )
      setTodos(
        todos.map((t) =>
          (t.id === updatedTodo.id || t.documentId === updatedTodo.documentId)
            ? updatedTodo
            : t
        )
      )
    } catch (err) {
      console.error("Error toggling todo:", err)
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri ažuriranju zadatka"
      )
    }
  }

  const handleDeleteTodo = async (id: number, documentId?: string) => {
    if (!token || hasNoEvents) return;

    try {
      setError(null)
      await deleteTodo(token, id, documentId)
      setTodos(todos.filter((t) => t.id !== id))
      setDeleteTodoId(null)
    } catch (err) {
      console.error("Error deleting todo:", err)
      setError(
        err instanceof Error
          ? err.message
          : "Greška pri brisanju zadatka"
      )
    }
  }

  const startEditing = (todo: Todo) => {
    setTodoTitleError(null)
    setEditingTodo(todo)
    setTodoTitle(todo.attributes?.title ?? todo.title ?? "")
    setIsEditDialogOpen(true)
  }

  const resetForm = () => {
    setTodoTitle("")
    setTodoTitleError(null)
    setEditingTodo(null)
  }

  // Calculate statistics
  const totalTodos = todos.length
  const completedTodos = todos.filter(
    (todo) => todo.attributes?.completed ?? todo.completed ?? false
  ).length
  const notCompletedTodos = totalTodos - completedTodos

  // Filter and sort todos
  const filteredAndSortedTodos = todos
    .filter((todo) => {
      const completed = todo.attributes?.completed ?? todo.completed ?? false
      if (filter === "completed") return completed
      if (filter === "not-completed") return !completed
      return true
    })
    .sort((a, b) => {
      const dateA = new Date(
        a.attributes?.createdAt ?? a.createdAt ?? 0
      ).getTime()
      const dateB = new Date(
        b.attributes?.createdAt ?? b.createdAt ?? 0
      ).getTime()
      return sort === "newest" ? dateB - dateA : dateA - dateB
    })

  const exportToPDF = async () => {
    if (hasNoEvents) return
    if (todos.length === 0) {
      setError("Nema zadataka za izvoz")
      return
    }

    try {
      const [{ jsPDF, autoTable }] = await Promise.all([loadPDF(), loadPDFLogo()])

      const doc = new jsPDF()
      const date = new Date().toLocaleDateString("sr-RS")
      let pageNum = 1

      drawPDFHeader(doc, "Zadaci", `Datum: ${date}`)

      // ── Stats row ────────────────────────────────────────────────────────
      let yPosition = drawStatRow(doc, [
        { label: "Ukupno",     value: String(totalTodos) },
        { label: "Završeno",   value: String(completedTodos) },
        { label: "Nezavršeno", value: String(notCompletedTodos) },
      ], 48)

      yPosition += 4

      // ── Table ─────────────────────────────────────────────────────────────
      const tableData = todos.map((todo) => {
        const title = todo.attributes?.title ?? todo.title ?? ""
        const completed = todo.attributes?.completed ?? todo.completed ?? false
        return [fixSerbianText(title), completed ? "Da" : "Ne"]
      })

      const base = getPDFTableStyles()
      const relaxed = withPDFRelaxedRows(base)
      autoTable(doc, {
        ...base,
        ...relaxed,
        startY: yPosition,
        head: [[fixSerbianText("Zadatak"), fixSerbianText("Uradjeno")]],
        body: tableData,
        columnStyles: {
          0: { cellWidth: 152, halign: "left", valign: "middle" },
          1: { cellWidth: 28, halign: "left", valign: "middle" },
        },
        didParseCell: (data: any) => {
          if (data.section === "body" && data.column.index === 1) {
            const done = data.cell.raw === "Da"
            data.cell.styles.fontStyle = "normal"
            data.cell.styles.textColor = done ? C.dark : C.gray
          }
        },
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            drawPDFHeader(doc, "Zadaci", `Datum: ${date}`)
          }
          drawPDFFooter(doc, pageNum + data.pageNumber - 1)
        },
      } as any)

      doc.save(`Zadaci-${new Date().toISOString().split("T")[0]}.pdf`)
    } catch (error) {
      console.error("Error exporting PDF:", error)
      setError("Greška pri konvertovanju u PDF")
    }
  }

  if (authLoading || loading) {
    return (
      <EventDataLoading className="min-h-screen flex flex-col items-center justify-center gap-8 px-3 py-4 bg-gradient-to-br from-background to-muted" />
    )
  }

  return (
    <div className="min-h-screen px-3 py-4 pb-20 lg:pb-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Zadaci</h1>
            <p className="text-muted-foreground mt-1">
              Upravljajte svojim zadacima
            </p>
            {hasNoEvents && (
              <PlannerEventRequiredNotice message="Da bi mogli da kreirate zadatke, morate prvo kreirati proslavu na profilu." />
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {todos.length > 0 && (
              <Button
                onClick={exportToPDF}
                variant="outline"
                className="w-full sm:w-auto"
                disabled={hasNoEvents}
              >
                <Download className="mr-2 h-4 w-4" />
                Konvertuj u PDF
              </Button>
            )}
            <Button
              onClick={() => {
                resetForm();
                setIsCreateDialogOpen(true);
              }}
              className="w-full sm:w-auto"
              disabled={hasNoEvents}
            >
              <Plus className="mr-2 h-4 w-4" />
              Novi zadatak
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-md border border-destructive/20">
            {error}
          </div>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 py-0">
              <div className="text-center">
                <p className="text-xl font-bold">{totalTodos}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Ukupno zadataka
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 py-0">
              <div className="text-center">
                <p className="text-xl font-bold text-[#065F46] dark:text-[#D1FAE5]">
                  {completedTodos}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Završeni zadaci
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 py-0">
              <div className="text-center">
                <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                  {notCompletedTodos}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Nezavršeni zadaci
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Sort */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="filter" className="mb-2 block">
              Filtriraj
            </Label>
            <Select
              value={filter}
              onValueChange={(value) => setFilter(value as FilterType)}
              disabled={hasNoEvents}
            >
              <SelectTrigger id="filter" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi zadaci</SelectItem>
                <SelectItem value="completed">Završeni</SelectItem>
                <SelectItem value="not-completed">Nezavršeni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label htmlFor="sort" className="mb-2 block">
              Sortiraj
            </Label>
            <Select
              value={sort}
              onValueChange={(value) => setSort(value as SortType)}
              disabled={hasNoEvents}
            >
              <SelectTrigger id="sort" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Najnoviji</SelectItem>
                <SelectItem value="oldest">Najstariji</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Todos List */}
        {filteredAndSortedTodos.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  {todos.length === 0
                    ? hasNoEvents
                      ? "Kreirajte proslavu na stranici Profil, zatim dodajte zadatke ovdje."
                      : "Lista zadataka je prazna. Dodajte novi zadatak klikom na dugme ispod."
                    : "Nema zadataka koji odgovaraju odabranom filteru."}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAndSortedTodos.map((todo) => {
              const completed =
                todo.attributes?.completed ?? todo.completed ?? false;
              const title = todo.attributes?.title ?? todo.title ?? "";
              const todoId = todo.documentId || todo.id;

              return (
                <div
                  key={todoId}
                  className={`p-4 rounded-lg border ${
                    completed
                      ? "bg-[#4bd968]/40 border-[#4bd968]/70 dark:bg-[#6ee67a]/30 dark:border-[#6ee67a]/50"
                      : "bg-card"
                  }`}
                >
                  <div className="flex items-center gap-4">
                      <Checkbox
                        checked={completed}
                        disabled={hasNoEvents}
                        onCheckedChange={() => handleToggleTodo(todo)}
                        className="shrink-0 w-5 h-5"
                      />
                      <div
                        className="flex-1 min-w-0 cursor-pointer select-none"
                        role="button"
                        tabIndex={hasNoEvents ? -1 : 0}
                        onClick={() => {
                          if (!hasNoEvents) void handleToggleTodo(todo)
                        }}
                        onKeyDown={(e) => {
                          if (
                            (e.key === "Enter" || e.key === " ") &&
                            !hasNoEvents
                          ) {
                            e.preventDefault()
                            void handleToggleTodo(todo)
                          }
                        }}
                      >
                        <h3
                          className={`font-semibold ${
                            completed
                              ? "line-through text-muted-foreground"
                              : ""
                          }`}
                        >
                          {title}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={hasNoEvents}
                          onClick={() => startEditing(todo)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10"
                              disabled={hasNoEvents}
                              onClick={() => setDeleteTodoId(todo.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Da li ste sigurni?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Ova akcija će obrisati zadatak "{title}". Ova
                                akcija se ne može poništiti.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel
                                onClick={() => setDeleteTodoId(null)}
                              >
                                Otkaži
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  handleDeleteTodo(todo.id, todo.documentId)
                                }
                                className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Obriši
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Single "Novi zadatak" button at the bottom */}
        <div className="flex justify-center">
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => resetForm()}
                variant="default"
                className="w-10 h-10 p-0"
                disabled={hasNoEvents}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Kreiraj novi zadatak</DialogTitle>
                <DialogDescription>
                  Dodajte novi zadatak na vašu listu
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="todo-title">Naziv zadatka *</Label>
                  <Input
                    id="todo-title"
                    placeholder="Unesite naziv zadatka..."
                    value={todoTitle}
                    onChange={(e) => {
                      setTodoTitle(e.target.value);
                      setTodoTitleError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && todoTitle.trim()) {
                        handleCreateTodo();
                      }
                    }}
                  />
                  {todoTitleError && (
                    <p className="text-destructive text-sm mt-1">
                      {todoTitleError}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter className="flex-row justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                    resetForm();
                  }}
                >
                  Otkaži
                </Button>
                <Button
                  onClick={handleCreateTodo}
                  disabled={hasNoEvents || !todoTitle.trim()}
                >
                  Kreiraj
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Izmjeni zadatak</DialogTitle>
              <DialogDescription>Ažurirajte naziv zadatka</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-todo-title">Naziv zadatka *</Label>
                <Input
                  id="edit-todo-title"
                  placeholder="Unesite naziv zadatka..."
                  value={todoTitle}
                  onChange={(e) => {
                    setTodoTitle(e.target.value);
                    setTodoTitleError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && todoTitle.trim()) {
                      handleUpdateTodo();
                    }
                  }}
                />
                {todoTitleError && (
                  <p className="text-destructive text-sm mt-1">
                    {todoTitleError}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  resetForm();
                }}
              >
                Otkaži
              </Button>
              <Button
                onClick={handleUpdateTodo}
                disabled={hasNoEvents || !todoTitle.trim()}
              >
                Sačuvaj izmjene
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

